/* eslint-disable react/prop-types */
import { Suspense, useContext } from 'react';
import { type InferType } from 'prop-types';
import classNames from 'classnames/bind';
import { MembershipEnum, PaymentSummary } from '@rivallapp/volosports-components';
import { gql, skipToken, useSuspenseQuery, type TypedDocumentNode } from '@apollo/client';

import styles from './styles.module.scss';
import { LeagueProps, GameProps } from '../../../../../types';
import { useRegistrationData } from '../../../../../hooks';
import { GenericErrorBoundary, Popup } from '../../../../../components';
import { RegistrationContext } from '../../../../../contexts';
import { CreditsAvailable } from '../../SharedComponents';
import PromoCodeForm from '../../../Register/RegisterSections/04-Payment/PromoCodeForm';
import Waiver from '../../../Register/RegisterSections/04-Payment/Waiver';
import AddPaymentCard from '../../../Register/RegisterSections/04-Payment/AddPaymentCard';
import { VPTermsAndConditionsPopup } from '../../../../../components/VPTermsAndConditionsPopup';
import usePaymentSummary from '../../../Register/hooks/usePaymentSummary';
import CancellationPolicyWaiver from '../CancellationPolicyWaiver';

type GameIdFromLeagueIdInput = { gameId: string };
type GameIdFromLeagueIdResponse = {
  game: {
    leagueId: string;
  };
};

const GAME_ID_FROM_LEAGUE_ID: TypedDocumentNode<
  GameIdFromLeagueIdResponse,
  GameIdFromLeagueIdInput
> = gql`
  query GameIdFromLeagueId($gameId: ID!) {
    game(_id: $gameId) {
      leagueId
    }
  }
`;

const cx = classNames.bind(styles);

type DailyPaymentSummaryProps = {
  league: InferType<typeof LeagueProps>;
  game: InferType<typeof GameProps>;
  acceptedTerms: boolean;
  setAcceptedTerms: React.Dispatch<React.SetStateAction<boolean>>;
  editPayment: boolean;
  setEditPayment: React.Dispatch<React.SetStateAction<boolean>>;
  totalDropInCents?: number;
  currentUserId: string;
  vpInCart: boolean;
};

const DailyPaymentSummary: React.FC<DailyPaymentSummaryProps> = props => {
  const {
    game,
    editPayment,
    setEditPayment,
    totalDropInCents = 0,
    currentUserId,
    vpInCart,
  } = props;

  const isDropIn = Boolean(game?._id);

  const { loading, creditAmountInCents, donationInCents } = usePaymentSummary(
    isDropIn ? 'dropin' : 'non-dropin'
  );

  // @ts-ignore - RegistrationContext doesn't have TS typing support.
  const [registerState] = useContext(RegistrationContext);

  const { league, paymentSources, pricingBreakdown, appliedPromo, usePaymentSummaryProps } =
    registerState;

  // This field doesn't apply to drop-ins. For drop-ins, we use the `totalDropInCents` prop.
  const { totalCents } = pricingBreakdown || {};

  // @ts-ignore - Downstream effect of RegistrationContext not having TS typings.
  const defaultSource = paymentSources?.find(p => p.isDefault && !p.isExpired);

  const { data } = useSuspenseQuery(
    GAME_ID_FROM_LEAGUE_ID,
    isDropIn
      ? {
          variables: {
            gameId: game?._id!,
          },
        }
      : skipToken
  );

  const {
    onStartVpClick,
    removeVpClick,
    registrationClosed,
    isRsvpdForDropIn,
    isEnrolledInProgram,
  } = useRegistrationData();

  const leagueId = league?._id ?? data?.game?.leagueId;
  if (!leagueId) {
    throw new Error('Could not fetch the associated league with this program!');
  }

  if (loading) return <PaymentSummary.Loading themeOverride="dark" />;

  return (
    <>
      {/* Payment Editor */}
      <Popup hidden={!editPayment} close={() => setEditPayment(false)}>
        <h3 className="pb-2">Edit Payment</h3>
        <AddPaymentCard />
        <CreditsAvailable />
      </Popup>

      <PaymentSummary
        themeOverride="dark"
        leagueId={leagueId}
        usePaymentSummaryProps={usePaymentSummaryProps}
        isDropIn={isDropIn}
        creditsAppliedInCents={creditAmountInCents ?? undefined}
        donationsAppliedInCents={donationInCents ?? undefined}
        promoCodeStr={appliedPromo}
        voloPassTermsAndConditions={<VPTermsAndConditionsPopup fontSize="xs" />}
        voloFitnessTermsAndConditions={undefined} // TODO
        onChangePricing={pricing => {
          if (pricing === MembershipEnum.VOLO_PASS) onStartVpClick();
          else removeVpClick();
        }}
        promoCodeComponent={
          <div className={cx('promoCodeFormText')}>
            <PromoCodeForm isDropIn={isDropIn} />
          </div>
        }
      />

      <div className={cx('paymentContainer')}>
        <div>
          {!registrationClosed && currentUserId && (
            <>
              <div className={cx('waiverText', 'mt-2 pt-1')}>
                <Waiver lightText />
              </div>
              <div className={cx('mt-2 pt-1')}>
                <CancellationPolicyWaiver />
              </div>
            </>
          )}
        </div>

        {!registrationClosed && (!isEnrolledInProgram || !isRsvpdForDropIn) && currentUserId && (
          <div className="row col-12 pt-3">
            {defaultSource && (vpInCart || (totalCents || totalDropInCents) > 0) ? (
              <p className="small">We&apos;ll charge the card ending in {defaultSource?.last4}.</p>
            ) : (
              <p className="small">{!defaultSource ? 'No card on file' : ''}</p>
            )}
            <div
              onClick={() => setEditPayment(true)}
              onKeyDown={() => setEditPayment(true)}
              role="button"
              tabIndex={0}
              className={cx('editPayment', 'pl-1')}
            >
              Edit
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const DailyPaymentSummaryWrapper = (props: DailyPaymentSummaryProps) => (
  <GenericErrorBoundary>
    <Suspense fallback={<PaymentSummary.Loading themeOverride="dark" />}>
      <DailyPaymentSummary {...props} />
    </Suspense>
  </GenericErrorBoundary>
);

export default DailyPaymentSummaryWrapper;
